import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { css } from "styled-components";

const UET_SCRIPT_FLAG = "data-uet-loaded";

let uetScriptRequested = false;

const buildUetScript = (tagId: string): string =>
    `(function(w,d,t,u,o){w[u]=w[u]||[],o.ts=(new Date).getTime();var n=d.createElement(t);` +
    `n.src="https://bat.bing.net/bat.js?ti="+o.ti+("uetq"!=u?"&q="+u:""),n.async=1,` +
    `n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&"loaded"!==s&&"complete"!==s||(` +
    `o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad"),n.onload=n.onreadystatechange=null)};` +
    `var i=d.getElementsByTagName(t)[0];i.parentNode.insertBefore(n,i);` +
    `})(window,document,"script","uetq",{ti:"${tagId}", enableAutoSpaTracking: true});`;

const loadUetScript = (tagId: string): void => {
    if (uetScriptRequested || document.querySelector(`script[${UET_SCRIPT_FLAG}]`)) {
        return;
    }
    uetScriptRequested = true;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.setAttribute(UET_SCRIPT_FLAG, "true");
    script.text = buildUetScript(tagId);
    document.head.appendChild(script);
};

const enum fields {
    tagId = "tagId",
}

export interface MicrosoftUetWidgetStyles {
    shellWrapper?: InjectableCss;
    shellTitle?: TypographyProps;
    shellTagId?: TypographyProps;
    shellNote?: TypographyProps;
}

export const microsoftUetWidgetStyles: MicrosoftUetWidgetStyles = {
    shellWrapper: {
        css: css`
            background: #f0f0f0;
            padding: 16px;
            border: 1px dashed #ccc;
        `,
    },
    shellTitle: {
        variant: "h6",
        css: css`margin: 0 0 8px 0;`,
    },
    shellTagId: {
        css: css`word-break: break-all;`,
    },
    shellNote: {
        css: css`margin-top: 8px; font-style: italic;`,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.tagId]: string;
    };
}

type Props = OwnProps & HasShellContext;

const styles = microsoftUetWidgetStyles;

const MicrosoftUetWidget = ({ fields, shellContext }: Props) => {
    const tagId = fields.tagId?.trim();

    useEffect(() => {
        if (IS_SERVER_SIDE || shellContext.isInShell || !tagId) {
            return;
        }

        loadUetScript(tagId);
    }, [tagId, shellContext.isInShell]);

    if (shellContext.isInShell && shellContext.isEditing) {
        return (
            <StyledWrapper {...styles.shellWrapper}>
                <Typography {...styles.shellTitle}>{translate("Microsoft UET Tag")}</Typography>
                {tagId ? (
                    <Typography {...styles.shellTagId}>{`Tag ID: ${tagId}`}</Typography>
                ) : (
                    <Typography {...styles.shellNote}>
                        {translate("Tag ID is not configured. Set the UET Tag ID field in the CMS.")}
                    </Typography>
                )}
                <Typography {...styles.shellNote}>
                    {translate("The tracking script runs on the live storefront, not in the CMS editor.")}
                </Typography>
            </StyledWrapper>
        );
    }

    return null;
};

const widgetModule: WidgetModule = {
    component: withIsInShell(MicrosoftUetWidget),
    definition: {
        group: "Basic",
        icon: "code",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.tagId,
                editorTemplate: "TextField",
                displayName: "UET Tag ID",
                defaultValue: "",
                tooltip: "Microsoft Advertising UET tag id (the 'ti' value). Digits only.",
                validate: value => {
                    if (!value) {
                        return null;
                    }
                    return /^\d+$/.test(value.trim()) ? null : "UET Tag ID must contain digits only";
                },
            },
        ],
    },
};

export default widgetModule;
